/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { UncontrolledTooltip } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import useApi from 'hooks/useApi';
import {
  paginateNotifications,
  setClearNotificationsList,
} from 'redux/actions/notificationActions';

import { ReactComponent as NotificationIcon } from 'assets/icon-notification.svg';
import { ReactComponent as FilterIcon } from 'assets/icon-filter.svg';
import { ReactComponent as FilterAppliedIcon } from 'assets/icon-filter-applied.svg';
import { ButtonIcon } from 'shared/components/Buttons';
import { ReactComponent as EditIcon } from 'assets/icon-edit.svg';
import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';
import { colors } from 'shared/defaultTheme';
import ConfirmModal from 'shared/components/ConfirmModal';
import AlertModal from 'shared/components/AlertModal';
import Pagination from 'shared/components/Pagination';
import FilterDropdownInput from 'shared/components/FilterDropdownInput';
import FilterDateSelect from 'shared/components/FilterDateSelect';
import MainSpinner from 'shared/components/MainSpinner';
import useNotification from 'hooks/useNotification';
import { TableNotification, TdAction, TdName, Td, Thead, Tbody, TBodyTr } from '../styles';

const PAGE_SIZE = 10;

const NotificationsTable = ({ handleRefresh, notificationsData, loading, setOpenFormModal }) => {
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const history = useHistory();
  const { isCustomer, clientId, mail } = useSelector((state) => state.auth);
  const notification = useSelector((state) => state.notification);
  const {
    notifications,
    currentNotifications,
    sortConfig,
    searchValue,
    clearList,
    allNotifications,
  } = notification;
  const [columnSearch, setColumnSearch] = useState({
    name: undefined,
    date: undefined,
  });
  const currentClient = useSelector((state) => state.clientState?.currentClient);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [SelectedRemoveNotifi, setSelectedRemoveNotifi] = useState(null);
  const [showConfirmRemoveNotifica, setShowConfirmRemoveNotifica] = useState(false);
  const [showSuccessRemove, setShowSuccessRemove] = useState();
  const [showErrorRemove, setShowErrorRemove] = useState(false);
  const [showFilterDate, setShowFilterDate] = useState(false);
  const [showFilterCreated, setShowFilterCreated] = useState(false);
  const { handleSelectNotificationReview } = useNotification();

  const [{ loading: loadingDelete }, deleteNotification] = customAreaApi(
    {
      method: 'DELETE',
    },
    { manual: true }
  );

  const onToggleErrorRemove = () => setShowErrorRemove((currentShow) => !currentShow);

  const onToggleSuccessRemove = () => setShowSuccessRemove((currentShow) => !currentShow);

  const onEdit = (currentNotification) => {
    handleSelectNotificationReview(currentNotification);
    const today = new Date();
    if (today >= new Date(currentNotification.date)) {
      history.push(`/notificacion_details`, { isClientNotify: false });
    } else {
      setOpenFormModal(true);
    }
  };

  const onRemove = (currentNotification) => {
    setSelectedRemoveNotifi(currentNotification);
    setShowConfirmRemoveNotifica(true);
  };

  const onOkSuccessRemove = () => {
    handleRefresh();
    setSelectedRemoveNotifi(null);
    onToggleSuccessRemove();
  };

  const onCancelRemove = () => {
    setShowConfirmRemoveNotifica(false);
    setSelectedRemoveNotifi(null);
  };

  const removeSuccess = () => {
    try {
      if (SelectedRemoveNotifi !== undefined && SelectedRemoveNotifi !== null) {
        setShowConfirmRemoveNotifica(false);
        onToggleSuccessRemove();
      }
    } catch (ex) {
      throw new Error(ex);
    }
  };

  const onOkRemove = async () => {
    try {
      const { clientNavisionId, id } = SelectedRemoveNotifi;
      if (loadingDelete) {
        return;
      }
      const {
        data: { errors },
      } = await deleteNotification({
        url: `${window.API_V1}/cli/notifications/${clientNavisionId}/${
          SelectedRemoveNotifi.clientId
        }/${encodeURIComponent(mail)}/${id}`,
      });
      if (errors.length) {
        throw new Error(
          errors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      removeSuccess();
    } catch (ex) {
      setShowConfirmRemoveNotifica(false);
      onToggleErrorRemove();
    }
  };

  const onFirstPage = () => {
    dispatch(paginateNotifications({ page: 1, pageSize: PAGE_SIZE }));
    setCurrentPage(1);
  };

  const onLastPage = () => {
    dispatch(paginateNotifications({ page: lastPage, pageSize: PAGE_SIZE }));
    setCurrentPage(lastPage);
  };

  const onNextPage = () => {
    const page = currentPage + 1;
    if (page > lastPage) {
      return;
    }
    dispatch(paginateNotifications({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  const onPreviousPage = () => {
    const page = currentPage - 1;
    if (page <= 0) {
      return;
    }
    dispatch(paginateNotifications({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  const onGoToPage = (page) => {
    dispatch(paginateNotifications({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  useEffect(() => {
    const currentLastPage = Math.ceil(notifications.length / PAGE_SIZE);
    onFirstPage();
    setLastPage(currentLastPage);
  }, [notifications]);

  const getOptionsFromAllNotifi = (fieldValue, fieldName) => {
    if (fieldValue === fieldName) {
      const values = Array.from(
        new Set(allNotifications.map((currentNotifi) => currentNotifi[fieldValue]))
      );
      return values.map((value) => ({ value, name: value }));
    }
    return allNotifications.map((currentNotifi) => ({
      value: currentNotifi[fieldValue],
      name: currentNotifi[fieldName],
    }));
  };

  const searchNotification = async (newColumnSearch) => {
    const searchParam =
      sortConfig.length > 0
        ? {
            filters: [
              ...sortConfig,
              {
                model: 'Notify',
                field: 'ClientId',
                filter: [isCustomer ? currentClient?.clientId : clientId],
              },
            ],
            reduce: {
              limit: 0,
              skip: 0,
            },
          }
        : {
            filters: [
              {
                model: 'Notify',
                field: 'ClientId',
                filter: [isCustomer ? currentClient?.clientId : clientId],
              },
            ],
            reduce: {
              limit: 0,
              skip: 0,
            },
          };
    Object.keys(newColumnSearch).forEach((key) => {
      if (newColumnSearch[key]) {
        searchParam.filters.push(newColumnSearch[key]);
      }
    });
    if (searchValue && searchValue.length >= 2) {
      searchParam.filterText = {
        regexFilter: searchValue,
      };
    }
    notificationsData(searchParam);
  };

  useEffect(() => {
    if (searchValue != null && !loading) {
      searchNotification(columnSearch);
    }
  }, [searchValue]);

  useEffect(() => {
    if (clearList) {
      setColumnSearch({
        name: undefined,
        date: undefined,
      });
      dispatch(setClearNotificationsList(false));
      searchNotification(columnSearch);
    }
  }, [clearList]);

  const onSearchOptionOrText = async ({
    selectedOption,
    searchText,
    fieldSelectedOption,
    fieldSearchText,
    column,
  }) => {
    const newColumnSearch = { ...columnSearch };
    newColumnSearch[column] = undefined;
    if (selectedOption) {
      newColumnSearch[column] = {
        model: 'Notify',
        field: fieldSelectedOption,
        filter: [selectedOption],
      };
    } else if (searchText) {
      newColumnSearch[column] = {
        model: 'Notify',
        field: fieldSearchText,
        filter: [searchText],
        isRegex: true,
      };
    }
    searchNotification(newColumnSearch);
    setColumnSearch(newColumnSearch);
  };

  const onSearchDate = async ({ dateFrom, dateTo, fieldSelectedOption, column }) => {
    const newColumnSearch = { ...columnSearch };
    newColumnSearch[column] = undefined;
    if (dateFrom && dateTo) {
      newColumnSearch[column] = {
        model: 'Notify',
        field: fieldSelectedOption,
        filter: [
          `${moment(dateFrom).format('YYYY-MM-DD')}T00:00:00.000Z`,
          `${moment(dateTo).format('YYYY-MM-DD')}T23:59:59.000Z`,
        ],
        IsLessThan: true,
      };
    }
    setShowFilterCreated(false);
    setShowFilterDate(false);
    searchNotification(newColumnSearch);
    setColumnSearch(newColumnSearch);
  };

  const handleTypeEventClick = (e, type, currentNotification) => {
    const { localName } = e.target;
    if (type === 'edit' && (localName === 'div' || localName === 'td')) {
      onEdit(currentNotification);
    }
    if (type === 'btnEdit') {
      onEdit(currentNotification);
    }
    if (type === 'delete') {
      onRemove(currentNotification);
    }
  };
  return (
    <div className="control-panel__table table-notifications">
      <TableNotification>
        <Thead>
          <tr>
            <th className="text-uppercase">
              {t('notification_column_issue')}
              <ButtonIcon
                title={t('general_button_filter')}
                id="filterNameColumn"
                className="button-icon"
                Icon={() =>
                  columnSearch.name ? (
                    <FilterAppliedIcon fill={colors.black} />
                  ) : (
                    <FilterIcon fill={colors.black} />
                  )
                }
                onClick={() => {
                  setShowFilterCreated(false);
                  setShowFilterDate(false);
                }}
              />
              <FilterDropdownInput
                dropdownOptions={getOptionsFromAllNotifi('name', 'name')}
                target="filterNameColumn"
                fieldSelectedOption="Name"
                fieldSearchText="Name"
                column="name"
                onSearch={onSearchOptionOrText}
                placement="bottom-start"
              />
            </th>
            <th className="text-uppercase">
              {t('notification_column_created_date')}
              <ButtonIcon
                title={t('general_button_filter')}
                id="filterCreatedColumn"
                className="button-icon"
                Icon={() =>
                  columnSearch.created ? (
                    <FilterAppliedIcon fill={colors.black} />
                  ) : (
                    <FilterIcon fill={colors.black} />
                  )
                }
                onClick={() => {
                  setShowFilterDate(false);
                }}
              />
              <FilterDateSelect
                dropdownOptions={getOptionsFromAllNotifi('created', 'created')}
                target="filterCreatedColumn"
                fieldSelectedOption="Created"
                column="created"
                onSearch={onSearchDate}
                showFilterDate={showFilterCreated}
                setShowFilterDate={setShowFilterCreated}
              />
            </th>
            <th className="text-uppercase">
              {t('notification_column_start_date')}
              <ButtonIcon
                title={t('general_button_filter')}
                id="filterDateColumn"
                className="button-icon"
                Icon={() =>
                  columnSearch.date ? (
                    <FilterAppliedIcon fill={colors.black} />
                  ) : (
                    <FilterIcon fill={colors.black} />
                  )
                }
                onClick={() => {
                  setShowFilterCreated(false);
                }}
              />
              <FilterDateSelect
                dropdownOptions={getOptionsFromAllNotifi('date', 'date')}
                target="filterDateColumn"
                fieldSelectedOption="Date"
                column="date"
                onSearch={onSearchDate}
                showFilterDate={showFilterDate}
                setShowFilterDate={setShowFilterDate}
              />
            </th>
            <th className="text-uppercase">{t('general_column_action')}</th>
          </tr>
        </Thead>
        <Tbody>
          {currentNotifications.map((currentNotification, index) => (
            <TBodyTr
              key={index}
              numAnswersUnread={currentNotification.numAnswersUnread}
              className="action-row-communication"
              onClick={(e) => handleTypeEventClick(e, 'edit', currentNotification)}
            >
              <TdName id={`notify-name-${index}`}>
                <div className="link-name-notification">
                  <p>{currentNotification.name}</p>
                </div>
                <UncontrolledTooltip
                  placement="top"
                  target={`notify-name-${index}`}
                  autohide={false}
                >
                  {currentNotification.name ?? ''}
                </UncontrolledTooltip>
              </TdName>
              <Td>{moment(currentNotification.created).format('DD/MM/YYYY HH:mm')}</Td>
              <Td>{moment(currentNotification.date).format('DD/MM/YYYY HH:mm')}</Td>
              <TdAction>
                <div className="d-flex">
                  <ButtonIcon
                    id={`edit-icon-notification-${index}`}
                    title={t('clients_edit_client')}
                    type="button"
                    Icon={() => <EditIcon fill={colors.suvaGrey} />}
                    className="button-icon"
                    onClick={(e) => handleTypeEventClick(e, 'btnEdit', currentNotification)}
                  />
                  {isCustomer && (
                    <>
                      <ButtonIcon
                        id={`trash-icon-notification-${index}`}
                        title={t('general_delete')}
                        type="button"
                        onClick={(e) => handleTypeEventClick(e, 'delete', currentNotification)}
                        Icon={() => <TrashIcon fill={colors.suvaGrey} />}
                        className="button-icon mr-n3 ml-2"
                        isDisabled={currentNotification.answers?.length > 0}
                      />
                    </>
                  )}
                </div>
              </TdAction>
            </TBodyTr>
          ))}
        </Tbody>
      </TableNotification>
      {notifications.length === 0 && (
        <div className="panel_control__empty-folder d-flex flex-column align-items-center">
          <NotificationIcon fill={colors.suvaGrey} width="71" />
          <span>{t('notification_not_found')}</span>
        </div>
      )}
      {notifications.length > 0 && (
        <Pagination
          titlePagination={t('notification_title')}
          currentPage={currentPage}
          lastPage={lastPage}
          totalData={notifications.length}
          onFirstPage={onFirstPage}
          onLastPage={onLastPage}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
          onGoToPage={onGoToPage}
        />
      )}
      <ConfirmModal
        isOpen={showConfirmRemoveNotifica}
        toggle={onCancelRemove}
        confirmMessage={t('notification_ask_delete_confirmation')}
        cancelText={t('general_button_no')}
        okText={t('general_button_yes')}
        onCancel={onCancelRemove}
        onOk={onOkRemove}
      />
      <AlertModal
        type="error"
        isOpen={showErrorRemove}
        toggle={onToggleErrorRemove}
        message={t('custom_field_delete_error_message')}
        okText={t('general_button_ok')}
        onOk={onToggleErrorRemove}
      />
      <AlertModal
        type="success"
        isOpen={showSuccessRemove}
        toggle={onOkSuccessRemove}
        message={t('notification_delete_confirmation')}
        okText={t('general_button_ok')}
        onOk={onOkSuccessRemove}
      />
      {(loading || loadingDelete) && (
        <div className="d-flex justify-content-center mb-5 align-items-center h-100">
          <MainSpinner />
        </div>
      )}
    </div>
  );
};

NotificationsTable.propTypes = {
  handleRefresh: PropTypes.func.isRequired,
  notificationsData: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  setOpenFormModal: PropTypes.func.isRequired,
};

export default NotificationsTable;
